import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, ExternalLink, Link as LinkIcon } from "lucide-react";

/**
 * XamanTrustline — one-tap CAUL trustline opener.
 *
 * Lifecycle:
 *   1. User clicks "Open CAUL trustline" → backend POST /caul-xrpl/xaman/trustset-payload
 *   2. Component renders Xaman QR + deep-link
 *   3. Polls /caul-xrpl/xaman/payload/{uuid} every 3s
 *   4. On signed=true → POST /caul-xrpl/xaman/trustset-confirm → backend verifies
 *      the trustline appears on XRPL and flips `xrplTrustlineActive=true`
 *   5. Calls `onConfirmed()` so parent (Wallet) refreshes
 */
export default function XamanTrustline({ disabled = false, onConfirmed = () => {} }) {
  const [payload, setPayload] = useState(null);
  const [polling, setPolling] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [airdrop, setAirdrop] = useState(null);

  const start = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const { data } = await api.post("/caul-xrpl/xaman/trustset-payload");
      if (!data?.uuid) {
        toast.error("Xaman didn't return a trustline payload");
        return;
      }
      setPayload(data);
      setPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start trustline flow");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!polling || !payload?.uuid) return;
    const t0 = Date.now();
    const interval = setInterval(async () => {
      if (Date.now() - t0 > 10 * 60 * 1000) {
        clearInterval(interval);
        setPolling(false);
        setStatus({ expired: true });
        return;
      }
      try {
        const { data } = await api.get(`/caul-xrpl/xaman/payload/${payload.uuid}`);
        if (data.resolved) {
          clearInterval(interval);
          setPolling(false);
          if (data.signed) {
            try {
              const confirm = await api.post("/caul-xrpl/xaman/trustset-confirm", { uuid: payload.uuid });
              if (confirm.data.trustlineActive) {
                toast.success("CAUL trustline open — ready to receive");
                setStatus({ active: true, txHash: confirm.data.txHash });
                if (confirm.data.welcomeAirdrop?.fired) {
                  setAirdrop(confirm.data.welcomeAirdrop);
                  toast.success(`+${confirm.data.welcomeAirdrop.amount} CAUL airdropped to your wallet`);
                  setTimeout(() => setAirdrop(null), 6000);
                }
              } else if (confirm.data.pending) {
                toast.message("Signed — XRPL is validating the trustline. Refresh in a few seconds.");
                setStatus({ pending: true, txHash: confirm.data.txHash });
              }
              onConfirmed(confirm.data);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Couldn't confirm trustline");
              setStatus({ error: e.response?.data?.detail || "confirm-failed" });
            }
          } else {
            setStatus({ rejected: true });
          }
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, payload, onConfirmed]);

  return (
    <div className="space-y-3" data-testid="xaman-trustline">
      {!payload && (
        <button onClick={start} disabled={loading || disabled}
          className="bg-primary text-primary-foreground px-5 py-2.5 uppercase tracking-[0.2em] text-xs rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="xaman-trustset-start-btn">
          {loading ? <><Loader2 size={12} className="animate-spin"/> Preparing trustline QR…</> : <><LinkIcon size={12}/> Open CAUL trustline</>}
        </button>
      )}

      {payload && polling && !status && (
        <div className="space-y-3" data-testid="xaman-trustset-qr">
          <img src={payload.qrPng} alt="Scan with Xaman to open CAUL trustline" className="w-56 h-56 mx-auto bg-white p-2 rounded-sm" data-testid="xaman-trustset-qr-img" />
          <div className="text-center text-xs text-foreground/65">
            Scan with Xaman, or
            {" "}
            <a href={payload.nextAlways} target="_blank" rel="noreferrer" className="text-secondary underline inline-flex items-center gap-1" data-testid="xaman-trustset-deep-link">
              open in Xaman <ExternalLink size={10}/>
            </a>
          </div>
          <div className="text-center text-[10px] text-foreground/45 uppercase tracking-wider">
            Waiting for trustline signature…
          </div>
        </div>
      )}

      {status?.active && (
        <div className="text-sm text-secondary inline-flex items-center gap-2" data-testid="xaman-trustset-active">
          <Check size={14}/> Trustline open · ready to receive CAUL
        </div>
      )}
      {airdrop && (
        <div className="parchment p-5 text-center animate-pulse" data-testid="welcome-airdrop-banner">
          <div className="label-mystic text-primary">Welcome airdrop</div>
          <div className="font-serif text-4xl text-secondary mt-2">+{airdrop.amount} CAUL</div>
          <div className="text-xs text-foreground/55 mt-2">on its way to your Xaman wallet</div>
          {airdrop.txHash && (
            <div className="text-[10px] text-foreground/35 font-mono mt-1">tx {airdrop.txHash.slice(0, 12)}…</div>
          )}
        </div>
      )}
      {status?.pending && (
        <div className="text-sm text-foreground/85 inline-flex items-center gap-2" data-testid="xaman-trustset-pending">
          <Loader2 size={14} className="animate-spin"/> Signed — XRPL is validating.
        </div>
      )}
      {status?.rejected && (
        <div className="text-sm text-foreground/65 inline-flex items-center gap-2" data-testid="xaman-trustset-rejected">
          <X size={14}/> Trustline signing was rejected.
        </div>
      )}
      {status?.expired && (
        <div className="text-sm text-foreground/65 inline-flex items-center gap-2" data-testid="xaman-trustset-expired">
          <X size={14}/> Trustline payload expired.
        </div>
      )}
    </div>
  );
}
